import React, { useMemo, useCallback } from 'react';
import { Box, BoxProps, Link } from '@mui/material';
import { Mention } from '../../types/mention';
import { useNavigate } from 'react-router-dom';

interface MentionDisplayProps extends BoxProps {
  content: string;
  mentions: Mention[];
}

const MentionDisplay: React.FC<MentionDisplayProps> = ({
  content,
  mentions,
  ...boxProps
}) => {
  const navigate = useNavigate();

  // Parse content into segments (text and mentions)
  const segments = useMemo(() => {
    if (!mentions || mentions.length === 0) {
      return [{ type: 'text' as const, content }];
    }

    // Sort mentions by start index
    const sortedMentions = [...mentions].sort((a, b) => a.startIndex - b.startIndex);

    const result: Array<
      | { type: 'text'; content: string }
      | { type: 'mention'; userId: string; username: string }
    > = [];

    let lastIndex = 0;

    for (const mention of sortedMentions) {
      // Add text before mention
      if (mention.startIndex > lastIndex) {
        result.push({
          type: 'text',
          content: content.substring(lastIndex, mention.startIndex),
        });
      }

      // Add mention
      result.push({
        type: 'mention',
        userId: mention.userId,
        username: mention.username,
      });

      lastIndex = mention.endIndex;
    }

    // Add remaining text
    if (lastIndex < content.length) {
      result.push({
        type: 'text',
        content: content.substring(lastIndex),
      });
    }

    return result;
  }, [content, mentions]);

  const handleMentionClick = useCallback((userId: string) => {
    // Navigate to user's linked person profile
    // In real implementation, you'd fetch the user's personId first
    navigate(`/person/${userId}`);
  }, [navigate]);

  return (
    <Box component="span" {...boxProps}>
      {segments.map((segment, index) => {
        if (segment.type === 'text') {
          return <span key={index}>{segment.content}</span>;
        } else {
          return (
            <Link
              key={index}
              component="button"
              variant="body2"
              onClick={() => handleMentionClick(segment.userId)}
              sx={{
                color: 'primary.main',
                fontWeight: 500,
                cursor: 'pointer',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              @{segment.username}
            </Link>
          );
        }
      })}
    </Box>
  );
};

export default MentionDisplay;
